import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import Calendar from './Calendar';
import toast from 'react-hot-toast';

const WEEKDAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 7, label: 'Sun' },
];

function TodayBoard({ students }) {
  const [status, setStatus] = useState({});

  useEffect(() => {
    let cancelled = false;
    Promise.all(students.map(async s => {
      try {
        const checklist = await api.getToday(s.id);
        return [s.id, { done: checklist.filter(c => c.done).length, total: checklist.length }];
      } catch {
        return [s.id, null];
      }
    })).then(entries => {
      if (!cancelled) setStatus(Object.fromEntries(entries.filter(([, v]) => v)));
    });
    return () => { cancelled = true; };
  }, [students]);

  const withClasses = students.filter(s => status[s.id]?.total > 0);
  if (withClasses.length === 0) return null;

  return (
    <div className="meds-section today-board">
      <div className="section-header"><h3>Today's Progress</h3></div>
      <ul className="today-board-list">
        {withClasses.map(s => {
          const st = status[s.id];
          const allDone = st.done === st.total;
          return (
            <li key={s.id} className={`today-board-item ${allDone ? 'all-done' : ''}`}>
              <span className="today-board-name">{s.name}</span>
              <span className="today-board-progress">{st.done} / {st.total}{allDone ? ' ✅' : ''}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function AdminView({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddClass, setShowAddClass] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', reminder_time: '15:00', timezone: 'America/Denver' });
  const [newClass, setNewClass] = useState({ name: '', instructions: '', schedule_days: [] });
  const [classStudents, setClassStudents] = useState({}); // classId -> [students]
  const [dayDetail, setDayDetail] = useState(null);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);

  const students = users.filter(u => u.role === 'student');

  useEffect(() => { loadUsers(); loadClasses(); }, []);

  async function loadUsers() {
    try {
      const data = await api.getUsers();
      setUsers(data);
      if (!selectedUser && data.length > 0) setSelectedUser(data.find(u => u.role === 'student') || data[0]);
    } catch { toast.error('Could not load users'); }
  }

  async function loadClasses() {
    try {
      setClasses(await api.getClasses());
    } catch { toast.error('Could not load classes'); }
  }

  const loadClassStudents = useCallback(async (classId) => {
    try {
      const list = await api.getClassStudents(classId);
      setClassStudents(prev => ({ ...prev, [classId]: list }));
    } catch { toast.error('Could not load class roster'); }
  }, []);

  useEffect(() => { classes.forEach(c => loadClassStudents(c.id)); }, [classes, loadClassStudents]);

  async function handleAddUser(e) {
    e.preventDefault();
    try {
      const user = await api.createUser(newUser);
      toast.success(`${user.name} invited — tell them to sign in at classcheck.billnicol.com with ${user.email}`);
      setShowAddUser(false);
      setNewUser({ name: '', email: '', reminder_time: '15:00', timezone: 'America/Denver' });
      loadUsers();
    } catch (err) { toast.error(err.message); }
  }

  async function handleDeleteUser(id) {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await api.deleteUser(id);
      toast.success('User deleted');
      setSelectedUser(null);
      loadUsers();
    } catch (err) { toast.error(err.message); }
  }

  function toggleDay(day) {
    setNewClass(nc => ({
      ...nc,
      schedule_days: nc.schedule_days.includes(day)
        ? nc.schedule_days.filter(d => d !== day)
        : [...nc.schedule_days, day].sort(),
    }));
  }

  async function handleAddClass(e) {
    e.preventDefault();
    try {
      await api.createClass(newClass);
      toast.success('Class added');
      setShowAddClass(false);
      setNewClass({ name: '', instructions: '', schedule_days: [] });
      loadClasses();
    } catch (err) { toast.error(err.message); }
  }

  async function handleDeleteClass(id) {
    if (!window.confirm('Deactivate this class?')) return;
    try {
      await api.deleteClass(id);
      toast.success('Class deactivated');
      loadClasses();
    } catch (err) { toast.error(err.message); }
  }

  async function handleToggleAssign(classId, studentId, isAssigned) {
    try {
      if (isAssigned) await api.unassignStudent(classId, studentId);
      else await api.assignStudent(classId, studentId);
      loadClassStudents(classId);
    } catch (err) { toast.error(err.message); }
  }

  async function handleDayClick(dateStr) {
    if (!selectedUser) return;
    setDayDetailLoading(true);
    try {
      setDayDetail(await api.getDay(selectedUser.id, dateStr));
    } catch { toast.error('Could not load that day'); }
    finally { setDayDetailLoading(false); }
  }

  return (
    <div className="admin-page">
      <TodayBoard students={students} />

      <div className="meds-section">
        <div className="section-header">
          <h3>Classes</h3>
          <button className="btn-small" onClick={() => setShowAddClass(!showAddClass)}>+ Add Class</button>
        </div>

        {showAddClass && (
          <form className="inline-form med-form" onSubmit={handleAddClass}>
            <input placeholder="Class name" value={newClass.name} onChange={e => setNewClass({ ...newClass, name: e.target.value })} required />
            <textarea placeholder="Instructions for students" value={newClass.instructions} onChange={e => setNewClass({ ...newClass, instructions: e.target.value })} rows={2} />
            <div className="weekday-picker">
              {WEEKDAYS.map(d => (
                <label key={d.value} className={`weekday-option ${newClass.schedule_days.includes(d.value) ? 'selected' : ''}`}>
                  <input type="checkbox" checked={newClass.schedule_days.includes(d.value)} onChange={() => toggleDay(d.value)} />
                  {d.label}
                </label>
              ))}
            </div>
            <button type="submit" className="btn-primary btn-small">Add</button>
          </form>
        )}

        <table className="meds-table">
          <thead>
            <tr><th>Name</th><th>Schedule</th><th>Instructions</th><th>Students</th><th></th></tr>
          </thead>
          <tbody>
            {classes.map(c => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.schedule_days.map(d => WEEKDAYS.find(w => w.value === d)?.label).join(', ') || '—'}</td>
                <td className="instructions-cell">{c.instructions || '—'}</td>
                <td>
                  <div className="assign-list">
                    {students.map(s => {
                      const assigned = (classStudents[c.id] || []).some(cs => cs.id === s.id);
                      return (
                        <label key={s.id} className="assign-checkbox">
                          <input type="checkbox" checked={assigned} onChange={() => handleToggleAssign(c.id, s.id, assigned)} />
                          {s.name}
                        </label>
                      );
                    })}
                  </div>
                </td>
                <td><button className="btn-link btn-danger-link" onClick={() => handleDeleteClass(c.id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-view">
        <div className="admin-sidebar">
          <div className="sidebar-header">
            <h2>Students</h2>
            <button className="btn-small" onClick={() => setShowAddUser(!showAddUser)}>+ Add</button>
          </div>

          {showAddUser && (
            <form className="inline-form" onSubmit={handleAddUser}>
              <input placeholder="Name" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} required />
              <input type="email" placeholder="Email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required />
              <input type="time" value={newUser.reminder_time} onChange={e => setNewUser({ ...newUser, reminder_time: e.target.value })} />
              <select value={newUser.timezone} onChange={e => setNewUser({ ...newUser, timezone: e.target.value })}>
                <option value="America/New_York">Eastern</option>
                <option value="America/Chicago">Central</option>
                <option value="America/Denver">Mountain</option>
                <option value="America/Los_Angeles">Pacific</option>
              </select>
              <button type="submit" className="btn-primary btn-small">Invite</button>
            </form>
          )}

          <ul className="user-list">
            {users.map(u => (
              <li key={u.id} className={`user-item ${selectedUser?.id === u.id ? 'active' : ''}`}>
                <span onClick={() => { setSelectedUser(u); setDayDetail(null); }}>{u.name}</span>
                <span className={`role-badge ${u.role}`}>{u.role}</span>
                <span className={`role-badge ${u.is_active ? 'admin' : 'member'}`}>
                  {u.is_active ? 'Active' : 'Invited'}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="admin-main">
          {selectedUser ? (
            <>
              <div className="admin-user-header">
                <h2>{selectedUser.name}</h2>
                <div className="admin-user-actions">
                  <button className="btn-secondary btn-small" onClick={() => api.exportCSV(selectedUser.id, selectedUser.name)}>⬇ Export CSV</button>
                  {selectedUser.id !== currentUser.id && (
                    <button className="btn-danger btn-small" onClick={() => handleDeleteUser(selectedUser.id)}>Delete User</button>
                  )}
                </div>
              </div>

              <div className="meds-section">
                <div className="section-header"><h3>{selectedUser.name}'s Calendar</h3></div>
                <Calendar key={`admin-cal-${selectedUser.id}`} userId={selectedUser.id} onDayClick={handleDayClick} />

                {dayDetailLoading && <div className="calendar-loading">Loading…</div>}
                {dayDetail && (
                  <div className="day-detail">
                    <h4>{dayDetail.date}</h4>
                    {dayDetail.classes.length === 0 ? (
                      <p className="empty-state">No classes scheduled this day.</p>
                    ) : (
                      dayDetail.classes.map(d => (
                        <div key={d.class_id} className={`day-detail-item ${d.done ? 'taken' : 'missed'}`}>
                          <span className="day-detail-dot" />
                          <span className="day-detail-name">{d.name}</span>
                          <span className="day-detail-status">
                            {d.done
                              ? `Done at ${new Date(d.completed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}${d.notes ? ` — "${d.notes}"` : ''}`
                              : 'Missed'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="empty-state">Select a student to view their details.</p>
          )}
        </div>
      </div>
    </div>
  );
}
